import { Alert, Button, Drawer, Empty, Message, Modal, Spin, Table, Tabs, Typography } from "@arco-design/web-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { FULFILLMENT_ORDER_PERMISSIONS } from "../../fulfillment-order/config";
import { useFulfillmentOrderReadOnlyRuntime } from "../../fulfillment-order/runtime/useFulfillmentOrderReadOnlyRuntime";
import { getSafeFulfillmentOrderActionErrorMessage } from "../../fulfillment-order/service";
import { createFulfillmentOrderLineColumns } from "../../fulfillment-order/ui/FulfillmentOrderLinePool";
import "../../fulfillment-order/ui/fulfillmentOrder.css";
import { fetchGuideLaborContract, generateGuideLaborContract } from "../repo";
import {
  buildGuideContractSigningPlan,
  groupActiveGuideLines,
} from "../service/groupGuideLines";
import type { GuideLaborContract } from "../types";

/** 合同交接导游页：法大大导游劳务电子签（ModelType=600）。 */
export function OrderHandoverGuidePanel({
  projectId,
  itineraryId,
}: {
  projectId: string;
  itineraryId: string;
}) {
  const user = useCurrentAuthUser().data;
  const canAccess = canUsePermission(user, FULFILLMENT_ORDER_PERMISSIONS.access);
  const canManage = canUsePermission(user, "customization.order_handover:manage");
  const runtime = useFulfillmentOrderReadOnlyRuntime(itineraryId, canAccess);
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeGuideKey, setActiveGuideKey] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeContract, setActiveContract] = useState<GuideLaborContract | null>(null);

  const editor = runtime.workspace;
  const groups = useMemo(
    () => groupActiveGuideLines(editor?.lines ?? [], editor?.orders ?? []),
    [editor?.lines, editor?.orders],
  );
  const resolvedGuideKey = useMemo(() => {
    if (groups.length === 0) return undefined;
    if (activeGuideKey && groups.some((group) => group.key === activeGuideKey)) {
      return activeGuideKey;
    }
    return groups[0]?.key;
  }, [activeGuideKey, groups]);
  const activeGroup = useMemo(
    () => groups.find((group) => group.key === resolvedGuideKey) ?? null,
    [groups, resolvedGuideKey],
  );
  const columns = useMemo(() => createFulfillmentOrderLineColumns(), []);
  const selectedCount = selectedIds.length;
  const settlementSupplierId = activeGroup?.settlementSupplierId ?? null;

  const contractQuery = useQuery({
    queryKey: ["orderHandover", "guideLaborContract", projectId, settlementSupplierId],
    queryFn: () => fetchGuideLaborContract(projectId, settlementSupplierId!),
    enabled: Boolean(canAccess && projectId && settlementSupplierId),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (contractQuery.data) {
      setActiveContract(contractQuery.data);
    } else if (contractQuery.isFetched) {
      setActiveContract(null);
    }
  }, [contractQuery.data, contractQuery.isFetched]);

  const handleViewContract = () => {
    if (!activeContract) {
      Message.warning("当前导游尚未生成劳务合同");
      return;
    }
    setDrawerVisible(true);
  };

  const handleGenerateContract = () => {
    if (!editor || !activeGroup || selectedCount === 0) return;
    const planResult = buildGuideContractSigningPlan(editor.lines, selectedIds);
    if (!planResult.ok) {
      Message.warning(planResult.error);
      return;
    }
    if (!canManage) {
      Message.warning("当前账号无生成合同权限");
      return;
    }
    const { plan } = planResult;
    const selectedLines = editor.lines.filter((line) => plan.lineIds.includes(line.id));
    Modal.confirm({
      title: activeContract ? "重新生成导游劳务合同" : "生成导游劳务合同",
      content: (
        <div>
          <p>
            将按导游「{plan.guideName}」本次选中的 {plan.selectedLineNos.length} 条费用
            生成法大大导游劳务电子签。
          </p>
          {activeContract ? (
            <p>原待签合同将作废，并按本次勾选重建（对齐老系统每次生成作废原合同）。</p>
          ) : null}
          <p>生成后提供签署链接；旅行社自动盖章，导游个人签署。</p>
        </div>
      ),
      okText: activeContract ? "作废并重新生成" : "确认生成",
      cancelText: "取消",
      onOk: async () => {
        setSubmitting(true);
        try {
          const contract = await generateGuideLaborContract(projectId, {
            settlementSupplierId: plan.settlementSupplierId,
            guideName: plan.guideName,
            feeLines: selectedLines.map((line) => ({
              itemName: line.itemNameSnapshot,
              unitPrice: line.unitPriceAmount,
              amount: line.amount,
              lineNo: line.lineNo,
              serviceDateFrom: line.serviceDateFrom,
              serviceDateTo: line.serviceDateTo,
            })),
            replaceExisting: true,
          });
          setSelectedIds([]);
          setActiveContract(contract);
          setDrawerVisible(true);
          await queryClient.invalidateQueries({
            queryKey: ["orderHandover", "guideLaborContract", projectId, plan.settlementSupplierId],
          });
          Message.success(`已为导游「${plan.guideName}」生成劳务合同`);
        } catch (error) {
          Message.error(getSafeFulfillmentOrderActionErrorMessage(error, "生成合同失败，请稍后重试"));
          throw error;
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  if (!canAccess) return <Alert type="warning" content="暂无确认件查看权限，无法读取导游明细" />;
  if (runtime.loading) return <Spin tip="正在加载导游明细" />;
  if (runtime.error) {
    return (
      <Alert
        type="error"
        content="导游明细加载失败"
        action={<Button onClick={() => void runtime.retry()}>重试</Button>}
      />
    );
  }
  if (!editor) return <Empty description="当前行程暂无确认件，无法展示导游明细" />;
  if (groups.length === 0) return <Empty description="当前确认件暂无导游费用行" />;

  return (
    <section className="order-handover-guide-panel" aria-label="导游">
      <header className="order-handover-guide-panel__header">
        <h3>导游费用（{groups.length} 人）</h3>
        <div className="order-handover-guide-panel__actions">
          <Typography.Text type="secondary">
            已选 {selectedCount} 条
          </Typography.Text>
          {activeContract ? (
            <Button type="outline" onClick={handleViewContract}>
              查看合同
            </Button>
          ) : null}
          <Button
            type="primary"
            loading={submitting}
            disabled={!canManage || selectedCount === 0}
            onClick={handleGenerateContract}
          >
            生成合同
          </Button>
        </div>
      </header>
      <Tabs
        activeTab={resolvedGuideKey}
        onChange={(key) => {
          setActiveGuideKey(key);
          setSelectedIds([]);
          setDrawerVisible(false);
        }}
        destroyOnHide
      >
        {groups.map((group) => (
          <Tabs.TabPane
            key={group.key}
            title={`${group.guideName}（${group.lines.length}）`}
          >
            <Table
              rowKey="id"
              columns={columns}
              data={group.lines}
              pagination={{
                pageSize: 10,
                showTotal: true,
                sizeCanChange: true,
              }}
              rowSelection={{
                type: "checkbox",
                selectedRowKeys: selectedIds.filter((id) =>
                  group.lines.some((line) => line.id === id),
                ),
                onChange: (keys) => {
                  setSelectedIds(keys as string[]);
                },
              }}
              scroll={{ x: 1274 }}
              aria-label={`${group.guideName}导游费用明细`}
            />
          </Tabs.TabPane>
        ))}
      </Tabs>
      <Drawer
        width={480}
        title="导游劳务合同"
        visible={drawerVisible}
        onCancel={() => setDrawerVisible(false)}
        footer={null}
      >
        {activeContract ? (
          <div className="order-handover-guide-panel__contract">
            <p>合同编号：{activeContract.contractNo}</p>
            <p>导游：{activeContract.guideName}</p>
            <p>
              状态：
              {activeContract.isSign
                ? "已签署"
                : activeContract.signState === 2
                  ? "待签署"
                  : `状态码 ${activeContract.signState}`}
            </p>
            {activeContract.signUrl && !activeContract.isSign ? (
              <Typography.Text type="secondary">
                请复制链接发给导游：先打开花儿签署页核对身份，完成法大大实名后再签署。勿把法大大未实名页当最终签署结果。
              </Typography.Text>
            ) : null}
            <div className="order-handover-guide-panel__contract-actions">
              {activeContract.signUrl ? (
                <>
                  <Button
                    type="primary"
                    onClick={async () => {
                      const url = activeContract.signUrl ?? "";
                      try {
                        await navigator.clipboard.writeText(url);
                        Message.success("签署链接已复制，请发给导游");
                      } catch {
                        Message.error("复制失败，请手动选中链接复制");
                      }
                    }}
                  >
                    复制签署链接
                  </Button>
                  <Button
                    type="outline"
                    onClick={() =>
                      window.open(activeContract.signUrl ?? "", "_blank", "noopener,noreferrer")
                    }
                  >
                    打开签署链接
                  </Button>
                </>
              ) : null}
              {activeContract.documentUrl ? (
                <Button
                  type="outline"
                  onClick={() =>
                    window.open(activeContract.documentUrl ?? "", "_blank", "noopener,noreferrer")
                  }
                >
                  查看合同
                </Button>
              ) : null}
              {activeContract.contractDownloadUrl ? (
                <Button
                  type="outline"
                  onClick={() =>
                    window.open(
                      activeContract.contractDownloadUrl ?? "",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  下载合同
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <Empty description="暂无劳务合同" />
        )}
      </Drawer>
    </section>
  );
}
